/* Greatly Social – CalendarPage.js  (v3.0 - Modernized UI)
   ----------------------------------------------------------------
   • UI modernized to match dashboard theme (dark sidebar, blue accents)
   • Custom react-big-calendar styles injected
   • Polished filter bar, modal, and event tiles
   • All logic identical to v2.4
   • v3.1 (Part E, T30): the view switch moved into the filter bar and
     gained 'Instagram grid' (./InstagramGrid.js, Grid.dc.html); the view
     and the grid's account are remembered in this browser
   ---------------------------------------------------------------- */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import moment from 'moment-timezone'

import { getPlatformIcon } from '../accounts/helpers/platformIcons'
import AccountDropdown  from '../../../components/AccountDropdown'
import StatusDropdown   from '../../../components/StatusDropdown'
import PlatformDropdown from '../../../components/PlatformDropdown'
import InstagramGrid    from './InstagramGrid'

/* ───────── backend root ───────── */
const ROOT     = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '')
const API_BASE = ROOT ? `${ROOT}/api` : '/api'

/* ───────── local-storage cache helpers ───────── */
const PLATFORM_LS_KEY  = 'gs_calendar_platforms'
const PLATFORM_TTL_MS  = 60 * 60 * 1000
const loadPlatformCache = () => {
  try {
    const rec = JSON.parse(localStorage.getItem(PLATFORM_LS_KEY) || '{}')
    if (rec.ts && Date.now() - rec.ts < PLATFORM_TTL_MS && Array.isArray(rec.list))
      return rec.list
  } catch {}
  return []
}
const savePlatformCache = list => {
  try {
    localStorage.setItem(PLATFORM_LS_KEY, JSON.stringify({ ts: Date.now(), list }))
  } catch {}
}

/* ───────── view switch (remembered in this browser) ───────── */
const VIEW_LS_KEY       = 'gs_calendar_view'
const GRID_ACCOUNT_KEY  = 'gs_calendar_grid_account'
const VIEW_LABELS = { month: 'Month', week: 'Week', day: 'Day', agenda: 'Agenda', grid: 'Instagram grid' }
const readLs  = key => { try { return localStorage.getItem(key) || '' } catch { return '' } }
const writeLs = (key, v) => { try { localStorage.setItem(key, v) } catch {} }

/* Calendar toolbar without its own view buttons (they live in the filter bar). */
function NavToolbar ({ label, onNavigate }) {
  return (
    <div className='rbc-toolbar'>
      <span className='rbc-btn-group'>
        <button type='button' onClick={() => onNavigate('TODAY')}>Today</button>
        <button type='button' onClick={() => onNavigate('PREV')}>Back</button>
        <button type='button' onClick={() => onNavigate('NEXT')}>Next</button>
      </span>
      <span className='rbc-toolbar-label'>{label}</span>
    </div>
  )
}

/* ───────── brand colors ───────── */
const PLATFORM_COLORS = {
  facebook  : '#1877F2',
  instagram : '#C32AA3',
  twitter   : '#1DA1F2',
  x         : '#000000',
  linkedin  : '#0077B5',
  pinterest : '#E60023',
  tiktok    : '#69C9D0',
  youtube   : '#FF0000',
}

/* ───────── calendar setup ───────── */
const localizer   = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(Calendar)

/* ───────── helpers ───────── */
const getCurrentUser = () =>
  window.currentUser ||
  JSON.parse(localStorage.getItem('user') || '{}') ||
  {}

/* ───────── injected calendar CSS ───────── */
const CALENDAR_CSS = `
  /* Base */
  .rbc-calendar {
    font-family: inherit;
    border: none !important;
  }

  /* Toolbar */
  .rbc-toolbar {
    padding: 0 0 16px 0;
    flex-wrap: wrap;
    gap: 8px;
  }
  .rbc-toolbar button {
    border: 1.5px solid #e5e7eb !important;
    border-radius: 8px !important;
    color: #374151 !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 6px 14px !important;
    background: #ffffff !important;
    transition: all 0.15s !important;
  }
  .rbc-toolbar button:hover {
    border-color: #2563eb !important;
    color: #2563eb !important;
    background: #eff6ff !important;
  }
  .rbc-toolbar button.rbc-active {
    background: #2563eb !important;
    color: #ffffff !important;
    border-color: #2563eb !important;
    box-shadow: none !important;
  }
  .rbc-toolbar-label {
    font-size: 16px !important;
    font-weight: 800 !important;
    color: #111827 !important;
  }

  /* Header row */
  .rbc-header {
    border-bottom: 1px solid #f3f4f6 !important;
    padding: 10px 0 !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.07em !important;
    color: #9ca3af !important;
  }

  /* Month grid */
  .rbc-month-view {
    border: none !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    background: #ffffff !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06) !important;
    border: 1px solid #e5e7eb !important;
  }
  .rbc-month-row {
    border-top: 1px solid #f3f4f6 !important;
    overflow: visible !important;
  }
  .rbc-day-bg {
    border-left: 1px solid #f3f4f6 !important;
    transition: background 0.1s !important;
  }
  .rbc-day-bg:hover {
    background: #fafafa !important;
  }
  .rbc-off-range-bg {
    background: #fafafa !important;
  }
  .rbc-today {
    background: #eff6ff !important;
  }
  .rbc-date-cell {
    font-size: 12px !important;
    font-weight: 600 !important;
    color: #6b7280 !important;
    padding: 6px 8px !important;
    text-align: right !important;
  }
  .rbc-date-cell.rbc-now {
    color: #2563eb !important;
    font-weight: 800 !important;
  }

  /* Week/Day views */
  .rbc-time-view {
    border: none !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    background: #ffffff !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06) !important;
    border: 1px solid #e5e7eb !important;
  }
  .rbc-time-header {
    border-bottom: 1px solid #f3f4f6 !important;
  }
  .rbc-time-slot {
    border-top: 1px solid #f9fafb !important;
    font-size: 11px !important;
    color: #9ca3af !important;
  }
  .rbc-timeslot-group {
    border-bottom: 1px solid #f3f4f6 !important;
    min-height: 44px !important;
  }
  .rbc-current-time-indicator {
    background: #2563eb !important;
    height: 2px !important;
  }

  /* Agenda view */
  .rbc-agenda-view table {
    border: none !important;
    font-size: 13px !important;
  }
  .rbc-agenda-date-cell, .rbc-agenda-time-cell {
    font-weight: 600 !important;
    color: #6b7280 !important;
    padding: 10px 14px !important;
  }
  .rbc-agenda-event-cell {
    padding: 10px 14px !important;
  }

  /* Event rows */
  .rbc-month-row .rbc-row-segment {
    width: 50% !important;
    display: inline-block !important;
    padding: 0 1px;
    box-sizing: border-box;
  }

  /* DnD drag ghost */
  .rbc-addons-dnd-drag-preview {
    opacity: 0.7 !important;
  }
`

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
export default function CalendarPage () {
  const navigate = useNavigate()

  const [events,  setEvents]  = useState([])
  const [userTz,  setUserTz]  = useState(getCurrentUser().timezone || moment.tz.guess())

  /* filters */
  const [filterStatus,   setFilterStatus]   = useState('scheduled')
  const [filterPlatform, setFilterPlatform] = useState('all')
  const [filterAccount,  setFilterAccount]  = useState('all')
  const [platformList,   setPlatformList]   = useState(loadPlatformCache)

  /* modal */
  const [showModal,      setShowModal]      = useState(false)
  const [selectedEvent,  setSelectedEvent]  = useState(null)
  const closeModal = () => { setShowModal(false); setSelectedEvent(null) }

  /* view: the calendar's own views plus 'grid' (Instagram grid) */
  const isMobile      = window.matchMedia('(max-width: 767.98px)').matches
  const calendarViews = isMobile ? ['week', 'day', 'agenda'] : ['month', 'week', 'day', 'agenda']
  const [view, setViewState] = useState(() => {
    const saved = readLs(VIEW_LS_KEY)
    return saved === 'grid' || calendarViews.includes(saved) ? saved : (isMobile ? 'week' : 'month')
  })
  const setView = v => { setViewState(v); writeLs(VIEW_LS_KEY, v) }

  /* Instagram grid account ('' = the first connected one) */
  const [gridAccounts,  setGridAccounts]  = useState([])
  const [gridShownId,   setGridShownId]   = useState('')
  const [gridAccountId, setGridAccountIdState] = useState(() => readLs(GRID_ACCOUNT_KEY))
  const setGridAccountId = id => { setGridAccountIdState(id); writeLs(GRID_ACCOUNT_KEY, id) }
  const handleGridAccounts = useCallback((list, shownId) => { setGridAccounts(list); setGridShownId(shownId) }, [])
  const handleGridAccountMissing = useCallback(() => { setGridAccountIdState(''); writeLs(GRID_ACCOUNT_KEY, '') }, [])

  /* inject CSS */
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = CALENDAR_CSS
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  /* fetch user timezone if missing */
  useEffect(() => {
    if (getCurrentUser().timezone) return
    ;(async () => {
      try {
        const { data } = await axios.get(`${API_BASE.replace(/\/api$/, '')}/api/auth/me`, { withCredentials: true })
        if (data?.user?.timezone) {
          window.currentUser = data.user
          localStorage.setItem('user', JSON.stringify(data.user))
          setUserTz(data.user.timezone)
        }
      } catch (e) {
        console.warn('Could not load user for timezone', e)
      }
    })()
  }, [])

  /* fetch posts */
  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/posting/all-posts`, { withCredentials: true })

        const shaped = (Array.isArray(data) ? data : []).map(p => {
          const raw = p.scheduled_at || p.post_date
          if (!raw) return null

          const start = moment.utc(raw).tz(userTz).toDate()
          const end   = moment(start).add(60, 'minutes').toDate()

          let errorMessage = null
          if (p.status === 'failed' && p.options) {
            try {
              const opts = typeof p.options === 'string' ? JSON.parse(p.options) : p.options
              if (opts.error) errorMessage = opts.error
            } catch (e) {}
          }

          return {
            id             : p.id,
            title          : '',
            start,
            end,
            mediaUrl       : p.media_url       || p.mediaUrl       || '',
            userAvatar     : p.user_avatar      || p.userAvatar     || '',
            platformIcon   : getPlatformIcon(p.platform),
            status         : p.status           || 'scheduled',
            platform       : p.platform         || '',
            accountId      : p.account_id       || p.accountId      || '',
            accountUsername: p.account_username || p.accountUsername || '',
            errorMessage,
          }
        }).filter(Boolean)

        setEvents(shaped)

        const list = Array.from(new Set(shaped.map(e => e.platform).filter(Boolean))).sort()
        if (list.join('|') !== loadPlatformCache().join('|')) {
          setPlatformList(list)
          savePlatformCache(list)
        }
      } catch (err) {
        console.error('Fetch posts failed', err)
      }
    })()
  }, [userTz])

  /* drag-and-drop reschedule */
  const handleEventDrop = async ({ event, start, end }) => {
    try {
      const utcForSave = moment(start).tz(userTz, true).utc().toISOString()
      await axios.put(
        `${API_BASE}/posting/scheduled-posts/${event.id}/time`,
        { scheduledAt: utcForSave },
        { withCredentials: true }
      )
      setEvents(evts => evts.map(e => e.id === event.id ? { ...e, start, end } : e))
    } catch (err) {
      alert('Could not reschedule.')
      console.error(err)
    }
  }

  /* event tile */
  const tileSize = 50
  function EventComponent ({ event }) {
    const isVideo  = /\.(mp4|mov|m4v|avi|wmv)$/i.test(event.mediaUrl || '')
    const isFailed = event.status === 'failed'

    return (
      <div
        title={isFailed ? `Failed: ${event.errorMessage || 'Unknown error'}` : ''}
        style={{
          width      : tileSize,
          height     : tileSize,
          position   : 'relative',
          borderRadius: 8,
          overflow   : 'hidden',
          background : '#f3f4f6',
          border     : isFailed ? '2px solid #ef4444' : '2px solid transparent',
          boxSizing  : 'border-box',
          boxShadow  : isFailed ? '0 0 0 2px rgba(239,68,68,0.2)' : '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        {event.mediaUrl && (
          isVideo ? (
            <video src={event.mediaUrl} muted preload='metadata'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              backgroundImage: `url("${encodeURI(event.mediaUrl)}")`,
              backgroundSize: 'cover', backgroundPosition: 'center',
            }} />
          )
        )}

        {/* Failed overlay */}
        {isFailed && (
          <div style={{
            position  : 'absolute', inset: 0,
            background: 'rgba(239,68,68,0.35)',
            display   : 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
          </div>
        )}

        {/* No media placeholder */}
        {!event.mediaUrl && !isFailed && (
          <div style={{
            position  : 'absolute', inset: 0,
            display   : 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#e5e7eb',
          }}>
            <i className='fas fa-file-alt' style={{ fontSize: 16, color: '#9ca3af' }} />
          </div>
        )}

        {/* Avatar */}
        {event.userAvatar && (
          <img src={event.userAvatar} alt=''
            style={{
              position: 'absolute', bottom: 2, left: 2,
              width: 14, height: 14, borderRadius: '50%',
              border: '1.5px solid #fff', objectFit: 'cover',
            }} />
        )}

        {/* Platform icon */}
        {event.platformIcon && (
          <i className={event.platformIcon}
            style={{
              position  : 'absolute', bottom: 2, right: 2,
              fontSize  : 11, color: '#fff',
              textShadow: '0 0 4px rgba(0,0,0,0.7)',
            }} />
        )}
      </div>
    )
  }

  const eventStyleGetter = () => ({
    style: { background: 'transparent', border: 'none', boxShadow: 'none', overflow: 'visible' },
  })

  const handleSelectEvent = ev => { setSelectedEvent(ev); setShowModal(true) }

  /* delete */
  const deletePost = async () => {
    if (!selectedEvent) return
    if (!window.confirm('Delete this scheduled post?')) return
    try {
      await axios.delete(`${API_BASE}/posting/scheduled-posts/${selectedEvent.id}`, { withCredentials: true })
      setEvents(prev => prev.filter(e => e.id !== selectedEvent.id))
      closeModal()
    } catch (err) {
      alert('Deletion failed.')
      console.error(err)
    }
  }

  /* edit */
  const editPost = () => {
    if (!selectedEvent) return
    navigate('/dashboard/post', { state: { editPostId: selectedEvent.id } })
  }

  /* filtering */
  const displayed = useMemo(() => events.filter(e => {
    if (filterStatus   !== 'all' && e.status    !== filterStatus)   return false
    if (filterPlatform !== 'all' && e.platform  !== filterPlatform) return false
    if (filterAccount  !== 'all' && e.accountId !== filterAccount)  return false
    return true
  }), [events, filterStatus, filterPlatform, filterAccount])

  /* accounts list for dropdown */
  const accountMap = {}
  events.forEach(e => {
    if (!e.accountId) return
    if (!accountMap[e.accountId])
      accountMap[e.accountId] = {
        id      : e.accountId,
        username: e.accountUsername,
        avatar  : e.userAvatar,
        icon    : getPlatformIcon(e.platform),
      }
  })
  const accountArray = Object.values(accountMap)

  /* ───────── render ───────── */
  return (
    <div>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
          Calendar
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          View, schedule, and drag-and-drop your posts across platforms.
        </p>
      </div>

      {/* Filter bar */}
      <div style={{
        display     : 'flex',
        gap         : 10,
        flexWrap    : 'wrap',
        alignItems  : 'center',
        background  : '#ffffff',
        border      : '1px solid #e5e7eb',
        borderRadius: 12,
        padding     : '12px 16px',
        marginBottom: 20,
        boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        {/* View switch (Grid.dc.html) */}
        <div role='group' aria-label='View' style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', flexWrap: 'wrap' }}>
          {[...calendarViews, 'grid'].map((v, i) => (
            <button
              key={v}
              type='button'
              aria-pressed={view === v}
              onClick={() => setView(v)}
              style={{
                padding   : '7px 14px',
                font      : 'inherit',
                fontSize  : 13,
                fontWeight: 600,
                border    : 0,
                borderLeft: i ? '1px solid #d1d5db' : 0,
                background: view === v ? '#2563eb' : '#fff',
                color     : view === v ? '#fff' : '#374151',
                cursor    : 'pointer',
              }}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>

        {view === 'grid' ? (
          <>
            <div style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', fontWeight: 600, margin: 0 }}>
              Account
              <select
                value={gridAccountId || gridShownId}
                onChange={e => setGridAccountId(e.target.value)}
                disabled={!gridAccounts.length}
                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, font: 'inherit', fontSize: 13, fontWeight: 500, background: '#fff' }}
              >
                {!gridAccounts.length && <option value=''>No Instagram account</option>}
                {gridAccounts.map(a => <option key={a.id} value={a.id}>@{a.username || a.id}</option>)}
              </select>
            </label>
          </>
        ) : (<>
        <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />

        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginRight: 4 }}>
          Filter
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Status</span>
          <StatusDropdown selectedStatus={filterStatus} onChange={setFilterStatus} />
        </div>

        <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Platform</span>
          <PlatformDropdown
            uniquePlatforms={platformList}
            selectedPlatform={filterPlatform}
            onChange={setFilterPlatform}
          />
        </div>

        <div style={{ width: 1, height: 20, background: '#e5e7eb' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Account</span>
          <AccountDropdown
            accounts={accountArray}
            selectedId={filterAccount}
            onChange={setFilterAccount}
          />
        </div>

        {/* Active filter count */}
        {(filterStatus !== 'scheduled' || filterPlatform !== 'all' || filterAccount !== 'all') && (
          <button
            onClick={() => { setFilterStatus('scheduled'); setFilterPlatform('all'); setFilterAccount('all') }}
            style={{
              marginLeft  : 'auto',
              fontSize    : 12,
              fontWeight  : 600,
              color       : '#6b7280',
              background  : 'transparent',
              border      : '1px solid #e5e7eb',
              borderRadius: 6,
              padding     : '4px 10px',
              cursor      : 'pointer',
            }}
          >
            Reset filters
          </button>
        )}
        </>)}
      </div>

      {view === 'grid' ? (
        /* Instagram grid (Part E, T30) */
        <InstagramGrid
          accountId={gridAccountId}
          onAccounts={handleGridAccounts}
          onAccountMissing={handleGridAccountMissing}
          userTz={userTz}
        />
      ) : (
        /* Calendar */
        <div style={{ height: '78vh' }}>
          <DnDCalendar
            localizer={localizer}
            events={displayed}
            startAccessor='start'
            endAccessor='end'
            components={{ event: EventComponent, toolbar: NavToolbar }}
            eventPropGetter={eventStyleGetter}
            view={view}
            onView={setView}
            views={calendarViews}
            step={30}
            timeslots={2}
            onEventDrop={handleEventDrop}
            resizable
            onSelectEvent={handleSelectEvent}
          />
        </div>
      )}

      {/* ── Custom Modal ── */}
      {showModal && selectedEvent && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeModal}
            style={{
              position  : 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex    : 1050,
              backdropFilter: 'blur(2px)',
            }}
          />

          {/* Modal panel */}
          <div style={{
            position     : 'fixed',
            top          : '50%',
            left         : '50%',
            transform    : 'translate(-50%, -50%)',
            zIndex       : 1051,
            background   : '#ffffff',
            borderRadius : 16,
            width        : 360,
            maxWidth     : 'calc(100vw - 32px)',
            boxShadow    : '0 20px 60px rgba(0,0,0,0.2)',
            overflow     : 'hidden',
          }}>
            {/* Header */}
            <div style={{
              display        : 'flex',
              alignItems     : 'center',
              justifyContent : 'space-between',
              padding        : '18px 20px 14px',
              borderBottom   : '1px solid #f3f4f6',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {selectedEvent.status === 'failed' ? (
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className='fas fa-exclamation' style={{ fontSize: 11, color: '#ef4444' }} />
                  </div>
                ) : (
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className='fas fa-calendar-check' style={{ fontSize: 11, color: '#2563eb' }} />
                  </div>
                )}
                <span style={{ fontSize: 15, fontWeight: 700, color: selectedEvent.status === 'failed' ? '#ef4444' : '#111827' }}>
                  {selectedEvent.status === 'failed' ? 'Post Failed' : 'Scheduled Post'}
                </span>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#9ca3af', fontSize: 16,
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px' }}>

              {/* Error message */}
              {selectedEvent.status === 'failed' && selectedEvent.errorMessage && (
                <div style={{
                  background  : '#fef2f2',
                  border      : '1px solid #fecaca',
                  borderRadius: 8,
                  padding     : '10px 14px',
                  marginBottom: 14,
                  fontSize    : 12,
                  color       : '#dc2626',
                  lineHeight  : 1.5,
                }}>
                  <strong>Error:</strong> {selectedEvent.errorMessage}
                </div>
              )}

              {/* Media preview */}
              {selectedEvent.mediaUrl && (
                <img
                  src={selectedEvent.mediaUrl}
                  alt=''
                  style={{
                    width: '100%', maxHeight: 160, objectFit: 'cover',
                    borderRadius: 10, marginBottom: 14,
                  }}
                />
              )}

              {/* Info rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                {/* Account */}
                {selectedEvent.userAvatar && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img
                      src={selectedEvent.userAvatar}
                      alt=''
                      style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb' }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                      {selectedEvent.accountUsername || 'Account'}
                    </span>
                  </div>
                )}

                {/* Platform */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#9ca3af', width: 60 }}>Platform</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {selectedEvent.platformIcon && (
                      <i
                        className={selectedEvent.platformIcon}
                        style={{ fontSize: 14, color: PLATFORM_COLORS[selectedEvent.platform] || '#6b7280' }}
                      />
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>
                      {selectedEvent.platform}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#9ca3af', width: 60 }}>Status</span>
                  <span style={{
                    fontSize    : 11,
                    fontWeight  : 700,
                    padding     : '2px 10px',
                    borderRadius: 99,
                    background  : selectedEvent.status === 'failed'   ? '#fef2f2'
                                : selectedEvent.status === 'posted'   ? '#dcfce7'
                                : '#eff6ff',
                    color       : selectedEvent.status === 'failed'   ? '#dc2626'
                                : selectedEvent.status === 'posted'   ? '#16a34a'
                                : '#2563eb',
                    textTransform: 'capitalize',
                  }}>
                    {selectedEvent.status}
                  </span>
                </div>

                {/* Date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#9ca3af', width: 60 }}>Date</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    {moment(selectedEvent.start).format('MMM D, YYYY [at] h:mm A')}
                  </span>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{
              display       : 'flex',
              gap           : 8,
              padding       : '14px 20px',
              borderTop     : '1px solid #f3f4f6',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={closeModal}
                style={{
                  padding     : '8px 16px',
                  borderRadius: 8,
                  border      : '1.5px solid #e5e7eb',
                  background  : 'transparent',
                  color       : '#374151',
                  fontSize    : 13,
                  fontWeight  : 600,
                  cursor      : 'pointer',
                }}
              >
                Close
              </button>
              <button
                onClick={deletePost}
                style={{
                  padding     : '8px 16px',
                  borderRadius: 8,
                  border      : 'none',
                  background  : '#fef2f2',
                  color       : '#dc2626',
                  fontSize    : 13,
                  fontWeight  : 700,
                  cursor      : 'pointer',
                }}
              >
                Delete
              </button>
              <button
                onClick={editPost}
                style={{
                  padding     : '8px 16px',
                  borderRadius: 8,
                  border      : 'none',
                  background  : '#2563eb',
                  color       : '#ffffff',
                  fontSize    : 13,
                  fontWeight  : 700,
                  cursor      : 'pointer',
                }}
              >
                {selectedEvent.status === 'failed' ? 'Edit & Retry' : 'Edit Post'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}